import { type SessionInfoResponse } from '@lume/protocol';
import { normalizeSessionCode } from '@lume/shared';
import {
  LumeClient,
  type LumeDisconnectReason,
  type LumePeerState,
} from '@lume/webrtc';
import { useEffect, useMemo, useRef, useState } from 'react';


import { CookieConsentBanner } from './components/cookie-consent-banner';
import { DebugOverlay } from './components/debug-overlay';
import { EndScreen } from './components/end-screen';
import { InvalidCodeScreen } from './components/invalid-code-screen';
import { LandingPage } from './components/landing-page';
import { LegalDocPage, resolveLegalDoc } from './components/legal';
import { SharingScreen } from './components/sharing-screen';
import { canShareScreen, UnsupportedBrowserScreen } from './components/unsupported-browser-screen';
import { WelcomeScreen } from './components/welcome-screen';

import { ApiClientError, fetchSessionInfo, joinSession } from '@/lib/api';
import { env } from '@/lib/env';

type AppState =
  | { kind: 'landing' }
  | { kind: 'unsupported' }
  | { kind: 'loading-info' }
  | { kind: 'invalid-code'; message?: string }
  | { kind: 'welcome'; info: SessionInfoResponse; isStarting: boolean; errorMessage?: string }
  | { kind: 'sharing'; info: SessionInfoResponse; state: LumePeerState }
  | {
      kind: 'ended';
      reason: LumeDisconnectReason;
      message?: string;
    };

export function App() {
  // Las rutas legales se resuelven antes que el código de sesión: sin esto,
  // `/privacidad` se interpretaría como un código y mostraría "enlace no válido".
  const legalDoc = useMemo(() => resolveLegalDoc(window.location.pathname), []);
  const { code, hasPath } = useMemo(
    () => (legalDoc ? { code: null, hasPath: false } : readCodeFromLocation()),
    [legalDoc],
  );
  const debugEnabled = useMemo(() => isDebugEnabled(), []);
  const [activeClient, setActiveClient] = useState<LumeClient | null>(null);
  // Bumped by the retry buttons to re-run the session lookup without a
  // full page reload, which would lose the screen-share permission prompt.
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<AppState>(() => {
    if (!hasPath) {
      return { kind: 'landing' };
    }
    if (code === null) {
      return { kind: 'invalid-code', message: 'El enlace no contiene un código válido.' };
    }
    // Checked before anything else: on a phone the whole flow is impossible,
    // and letting the customer reach the share button just produces a
    // failure they cannot act on.
    if (!canShareScreen()) {
      return { kind: 'unsupported' };
    }
    return { kind: 'loading-info' };
  });

  const retry = (): void => {
    if (code === null) {
      return;
    }
    setState({ kind: 'loading-info' });
    setAttempt((n) => n + 1);
  };
  const clientRef = useRef<LumeClient | null>(null);

  useEffect(() => {
    if (!code || !canShareScreen()) {
      return;
    }
    let cancelled = false;
    fetchSessionInfo(code)
      .then((info) => {
        if (cancelled) {
          return;
        }
        setState({ kind: 'welcome', info, isStarting: false });
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        setState({ kind: 'invalid-code', message: customerMessage(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [code, attempt]);

  useEffect(() => {
    return () => {
      clientRef.current?.disconnect('user');
      clientRef.current = null;
    };
  }, []);

  const handleStart = async (info: SessionInfoResponse): Promise<void> => {
    setState({ kind: 'welcome', info, isStarting: true });

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'monitor' },
        audio: false,
      });
    } catch (err) {
      const message =
        err instanceof Error && err.name === 'NotAllowedError'
          ? 'Has cancelado la compartición. Pulsa de nuevo cuando quieras intentarlo.'
          : 'No se ha podido acceder a la pantalla.';
      setState({ kind: 'welcome', info, isStarting: false, errorMessage: message });
      return;
    }

    let joinResponse;
    try {
      joinResponse = await joinSession(info.code, {});
    } catch (err) {
      stream.getTracks().forEach((t) => t.stop());
      setState({ kind: 'welcome', info, isStarting: false, errorMessage: customerMessage(err) });
      return;
    }

    const client = new LumeClient({
      signalingUrl: joinResponse.signalingUrl ?? env.signalingUrl,
      joinToken: joinResponse.joinToken,
      sessionCode: joinResponse.session.code,
      iceServers: joinResponse.iceServers as RTCIceServer[],
      stream,
    });
    clientRef.current = client;
    setActiveClient(client);

    client.on('stateChange', ({ state: peerState }) => {
      setState({ kind: 'sharing', info, state: peerState });
    });
    client.on('disconnect', ({ reason, message }) => {
      setState({ kind: 'ended', reason, message });
      clientRef.current = null;
      setActiveClient(null);
    });

    void client.connect();
  };

  const handleStop = (): void => {
    clientRef.current?.disconnect('user');
  };

  // Se comprueba después de todos los hooks para no romper las Rules of Hooks.
  if (legalDoc) {
    return <LegalDocPage doc={legalDoc} />;
  }

  if (state.kind === 'landing') {
    return (
      <>
        <LandingPage />
        <CookieConsentBanner />
      </>
    );
  }

  if (state.kind === 'loading-info') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 py-12">
        <p className="text-sm text-muted-foreground">Cargando sesión...</p>
      </div>
    );
  }

  if (state.kind === 'unsupported') {
    return <UnsupportedBrowserScreen code={code ?? ''} />;
  }

  if (state.kind === 'invalid-code') {
    return <InvalidCodeScreen message={state.message} onRetry={code ? retry : undefined} />;
  }

  if (state.kind === 'welcome') {
    return (
      <WelcomeScreen
        info={state.info}
        onStart={() => void handleStart(state.info)}
        isStarting={state.isStarting}
        errorMessage={state.errorMessage}
      />
    );
  }

  if (state.kind === 'sharing') {
    return (
      <>
        <SharingScreen info={state.info} state={state.state} onStop={handleStop} />
        {debugEnabled && activeClient ? <DebugOverlay client={activeClient} /> : null}
      </>
    );
  }

  return <EndScreen reason={state.reason} message={state.message} onRetry={retry} />;
}

/**
 * Message to show the customer for a failure coming out of `lib/api`.
 *
 * Everything that layer throws is already written for a non-technical Spanish
 * speaker, so the previous behaviour of replacing it with a generic line threw
 * away the useful half ("pide otro enlace", "espera un minuto"). The fallback
 * only catches genuinely unexpected throws.
 */
function customerMessage(err: unknown): string {
  if (err instanceof ApiClientError || err instanceof Error) {
    return err.message;
  }
  return 'No se ha podido cargar la sesión. Vuelve a intentarlo.';
}

/**
 * Smoke-test affordance: append `?debug=1` to the session URL to render
 * the live bitrate + negotiated codec overlay. No-op for normal users.
 */
function isDebugEnabled(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('debug') === '1';
  } catch {
    return false;
  }
}

/**
 * Distinguishes the apex (`/` → marketing landing) from a session URL
 * (`/<code>` → share flow). Returns `hasPath: false` only for the
 * apex; any other path tries to be a session code.
 *
 * Legal routes (`/aviso-legal`, `/privacidad`, ...) are resolved by
 * `resolveLegalDoc` before this function ever runs.
 */
function readCodeFromLocation(): { code: string | null; hasPath: boolean } {
  const segments = window.location.pathname.split('/').filter(Boolean);
  if (segments.length === 0) {
    return { code: null, hasPath: false };
  }
  return { code: normalizeSessionCode(segments[0] as string), hasPath: true };
}
